<script setup lang="ts">
import CheckmarkIcon from '@/components/icons/CheckmarkIcon.vue';

export type LaunchWizardStepKey = 'setup' | 'host' | 'configure-host' | 'billing' | 'review';
export type LaunchWizardHostingType = 'clovyr' | 'byoh';

interface StepDefinition {
  key: LaunchWizardStepKey;
  label: string;
}

const props = defineProps<{
  /** The step the wizard is currently showing. */
  currentStep: LaunchWizardStepKey;
  /**
   * The resolved hosting path. 'clovyr' = Clovyr Managed (Setup, Billing, Review),
   * 'byoh' = Bring Your Own Host (Setup, Host, Configure Host, Review).
   */
  hostingType: LaunchWizardHostingType;
}>();

const CLOVYR_MANAGED_STEPS: StepDefinition[] = [
  { key: 'setup', label: 'Setup' },
  { key: 'billing', label: 'Billing' },
  { key: 'review', label: 'Review' },
];

const BYOH_STEPS: StepDefinition[] = [
  { key: 'setup', label: 'Setup' },
  { key: 'host', label: 'Host' },
  { key: 'configure-host', label: 'Configure Host' },
  { key: 'review', label: 'Review' },
];

const steps = computed(() => (props.hostingType === 'byoh' ? BYOH_STEPS : CLOVYR_MANAGED_STEPS));

const currentIndex = computed(() => {
  const idx = steps.value.findIndex((step) => step.key === props.currentStep);
  return idx === -1 ? 0 : idx;
});

type StepStatus = 'completed' | 'current' | 'upcoming';

function statusFor(index: number): StepStatus {
  if (index < currentIndex.value) {
    return 'completed';
  }
  if (index === currentIndex.value) {
    return 'current';
  }
  return 'upcoming';
}

const statusLabels: Record<StepStatus, string> = {
  completed: 'completed',
  current: 'current step',
  upcoming: 'upcoming',
};
</script>

<template>
  <ol class="launch-wizard-progress" aria-label="Launch wizard progress">
    <li
      v-for="(step, index) in steps"
      :key="step.key"
      class="launch-wizard-progress__step"
      :class="`launch-wizard-progress__step--${statusFor(index)}`"
      :aria-current="statusFor(index) === 'current' ? 'step' : undefined"
    >
      <span class="launch-wizard-progress__marker">
        <span class="launch-wizard-progress__bubble">
          <CheckmarkIcon
            v-if="statusFor(index) === 'completed'"
            class="launch-wizard-progress__check"
            aria-hidden="true"
          />
        </span>
        <span class="launch-wizard-progress__label">
          {{ step.label }}
          <span class="visually-hidden">({{ statusLabels[statusFor(index)] }})</span>
        </span>
      </span>
      <span
        v-if="index < steps.length - 1"
        class="launch-wizard-progress__connector"
        :class="{
          'launch-wizard-progress__connector--completed': statusFor(index) === 'completed',
        }"
        aria-hidden="true"
      ></span>
    </li>
  </ol>
</template>

<style scoped lang="scss">
@use 'sass:color';
@use 'styles/core/colors';
@use 'styles/core/layout/grid-vars';
@use 'styles/vendor/bootstrap/scss/mixins/breakpoints';

.launch-wizard-progress {
  display: flex;
  align-items: flex-start;
  width: 100%;
  margin: 0;
  padding: 0;
  list-style: none;

  &__step {
    display: flex;
    align-items: center;
    flex: 1 1 auto;
    min-width: 0;

    &:last-child {
      flex: 0 0 auto;
    }
  }

  &__marker {
    display: flex;
    align-items: center;
    flex-shrink: 0;
    gap: grid-vars.space(1);
  }

  &__bubble {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 24px;
    height: 24px;
    flex-shrink: 0;
    border-radius: 50%;
    border: 2px solid colors.color(grey, primary);
    background-color: transparent;
    transition:
      border-color 200ms ease-in-out,
      background-color 200ms ease-in-out;
  }

  &__check {
    width: 11px;
    height: auto;

    :deep(path) {
      stroke: colors.color(negative);
    }
  }

  &__label {
    font-size: 0.875rem;
    font-weight: 500;
    color: colors.color(grey, primary);
    white-space: nowrap;
    transition: color 200ms ease-in-out;

    @include breakpoints.media-breakpoint-down(sm) {
      font-size: 0.75rem;
    }
  }

  &__connector {
    flex: 1 1 auto;
    height: 2px;
    min-width: grid-vars.space(1);
    margin: 0 grid-vars.space(1);
    background-color: colors.color(grey, primary);
    opacity: 0.35;
    transition:
      background-color 200ms ease-in-out,
      opacity 200ms ease-in-out;

    @include breakpoints.media-breakpoint-down(sm) {
      margin: 0 grid-vars.space(0.5);
    }

    &--completed {
      background-color: colors.color(action, primary);
      opacity: 1;
    }
  }

  &__step--current &__bubble {
    border-color: colors.color(action, primary);
    background-color: color.adjust(colors.color(action, primary), $alpha: -0.85);
  }

  &__step--current &__label {
    color: colors.color(white, primary);
    font-weight: 700;
  }

  &__step--completed &__bubble {
    border-color: colors.color(action, primary);
    background-color: colors.color(action, primary);
  }

  &__step--completed &__label {
    color: colors.color(action, primary);
  }
}

.visually-hidden {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
}
</style>
